import { useCountDown } from 'ahooks';
import { Tooltip, message } from 'antd';
import { reduce } from 'lodash';
import { formatPointsByLocale, getLang } from 'lowcomp/plugins/member/utils';
import moment from 'moment';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { TextLang } from './TextLang';

import { Button } from '@/components/UI';
import EcoImg from 'eco-design/base/eco-img';
import { CopyIcon, ExpandArrowBottom } from '@/components/UI/svg/Ecocredits';
import { FormatMoney } from '@/utils';
import { useGlobal } from '@/utils/useGlobal';
import css from './index.module.scss';

interface CouponModel {
    coupon?: string; // 优惠券劵code
    amount?: string; // 优惠券金额
    currencySymbol?: string; // 货币符号
    points?: number; // 兑换优惠券所需积分数量
    campaignId?: string; // 活动id
    campaignEndTime: string; // 活动结束时间
    campaignName: string; // 活动名称
    discountType: 'fixed_amount' | 'percentage'; // 折扣类型
    ruleType: string; // 积分类型(COUPONS;FREE_SHIPPING;FREE_PRODUCTS;GIFT_CARD)
    birthdayFlag?: 0 | 1; // 0 禁用 1启用
    source: string; // 积分场景(COUPONS_PRODUCTS;COUPONS_ORDERS)
    source2: string; // 积分场景2
    picUrl?: string; // 后台上传icon
    rulePic?: string; // 后台上传产品图片
    useStatus?: string;
    goodTitle?: string; // 产品名称
    couponName?: string; // 第三方优惠券名称
    orderName?: string; // 产品订单id
    receiveStatus?: 1 | 2 | 3;
    stock?: number; // 库存
    discountId?: string; // discountId
    variantTitle?: string; // 变体名称
    useUrl?: string; // 第三方优惠券跳转链接
    showCountdown: boolean; // 是否展示倒计时
    endTime?: string; // 优惠券失效时间

    // 前端添加状态
    minimum_rule?: string;
    noteList: string[];
}

interface CouponProps {
    coupon: CouponModel;
    className?: string;
    disabled?: boolean;
    disabledBtn?: boolean;
    btnText?: string;
    hideNote?: boolean;
    hideBtn?: boolean;
    hideAmount?: boolean;
    hideCopyIcon?: boolean;
    showCountDown?: boolean;
    showstock?: boolean;
    showJumpBtn?: boolean;
    onReedem?: (coupon: CouponModel) => void | Promise<any>;
    onViewsRules?: (rules: CouponModel) => void;
    onClickCard?: (coupon: CouponModel) => void;
    customColor?: Record<string, string | undefined>;
    customStyle?: Record<string, string>;
    modal?: boolean;
}

const BG_IMAGES = {
    COUPONS: {
        USED_PC: 'https://websiteoss.ecoflow.com/figma-uploads/1769160888264-ehjd09-compressed-Frame%201612706635.png',
        // DEFAULT: 'https://websiteoss.ecoflow.com/upload/image/1769743531471/Frame%202094654996.png',
        DEFAULT: 'https://websiteoss.ecoflow.com/figma-uploads/1769149590133-pknxkh-compressed-Frame%201612706636.png',
    },
    OTHERS: {
        // MOBILE: 'https://websiteoss.ecoflow.com/upload/image/1769743369762/Frame%202094654977.png',
        MOBILE: 'https://websiteoss.ecoflow.com/figma-uploads/1769149590133-pknxkh-compressed-Frame%201612706636.png',
        PC: 'https://websiteoss.ecoflow.com/figma-uploads/1769149590133-pknxkh-compressed-Frame%201612706636.png',
    },
};

const getConponBg = (coupon: CouponModel, isMobile: boolean) => {
    const { ruleType, useStatus, receiveStatus = 1 } = coupon || {};
    const isUsed = (useStatus && useStatus !== 'UNUSED') || (receiveStatus && receiveStatus !== 1);

    if (isUsed) {
        return BG_IMAGES.COUPONS.USED_PC;
    }

    if (ruleType === 'COUPONS') {
        return BG_IMAGES.COUPONS.DEFAULT;
    }

    return isMobile ? BG_IMAGES.OTHERS.MOBILE : BG_IMAGES.OTHERS.PC;
};

const isAllTrue = (...params: any) => {
    const arr = Array.from(params);
    return arr.every((i) => !!i);
};

const Coupon: FC<CouponProps> = ({
    coupon,
    className,
    disabled,
    disabledBtn,
    btnText,
    hideNote,
    hideBtn,
    hideAmount,
    showCountDown,
    hideCopyIcon = false,
    showstock = false,
    showJumpBtn = false,
    onReedem,
    onViewsRules,
    onClickCard,
    customColor,
    customStyle,
    modal,
}) => {
    const { useState, isMobile, Link, gtm } = useGlobal();
    const { locale, pathname } = useRouter();
    const t: any = getLang(TextLang, locale as string);

    const [loading, setLoading] = useState(false);

    // 倒计时
    const { campaignEndTime } = coupon;
    const isStartCountDown = showCountDown && !coupon?.discountId && coupon?.campaignEndTime;
    const targetDate = isStartCountDown ? moment(Number(campaignEndTime)).toString() : undefined;
    const [_countdown, { days, hours, minutes, seconds }] = useCountDown({ targetDate });

    const ruleTypeMap: { [key: string]: string } = {
        COUPONS: coupon?.couponName || t('coupon'),
        GIFT_CARD: t('gift_card'),
    };

    const useStatusMap: { [key: string]: string } = {
        UNUSED: t('available'),
        USED: t('redeemed'),
        EXPIRED: t('expired'),
    };

    const receiveStatusMap: { [key: string]: string } = {
        1: 'UNUSED',
        2: 'USED',
        3: 'EXPIRED',
    };

    const { useStatus, receiveStatus } = coupon || {};

    const useStatusC = useStatus ? useStatus : receiveStatusMap[receiveStatus || 1];

    const getCouponName = (coupon: CouponModel) => {
        const { ruleType, goodTitle, variantTitle, couponName } = coupon || {};

        // 优先使用couponName字段
        if (couponName) {
            return couponName;
        } else if (ruleTypeMap[ruleType]) {
            // 优惠券&礼品卡
            return ruleTypeMap[ruleType];
        } else if (ruleType === 'FREE_PRODUCTS') {
            // 实体产品
            return variantTitle ? `${goodTitle}-${variantTitle}` : goodTitle;
        } else {
            return '';
        }
    };

    const _onReedem = async (coupon: CouponModel) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_redeem',
            page_name: `/${locale}${pathname}`,
            list_name: 'Redeem EcoCredits Rewards',
            content: coupon?.amount,
            button_name: t('redeem_now'),
        });
        if (!onReedem) return;

        setLoading(true);
        await onReedem(coupon);
        setLoading(false);
    };

    const copyText = (text?: string) => {
        if (!text) return;

        if (navigator?.clipboard && window?.isSecureContext) {
            window.navigator.clipboard.writeText(text);
            message.success(t('site_account_profile_save_success'));
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'absolute';
            textArea.style.opacity = '0';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            new Promise(() => {
                const copyStatus = document.execCommand('copy');
                if (!copyStatus) {
                    // message.error()
                }
                textArea.remove();
            });
            message.success(t('site_account_profile_save_success'));
        }
    };

    const statusClassNames = {
        hideNote,
        hideBtn,
        disabled,
        showJumpBtn,
    };

    const formatTime = (time?: number) => {
        if (typeof time !== 'number') {
            return time;
        }

        if (time < 10 && time >= 0) {
            return `0${time}`;
        }

        return time;
    };

    const cls = reduce(
        statusClassNames,
        (res, val, key) => {
            if (val) {
                return `${res} ${key}`;
            }
            return res;
        },
        '',
    );

    return (
        <div
            className={`${css.coupon_card} ${coupon.ruleType} ${useStatusC} ${className} ${cls} ${customColor?.themeColor ? 'custom-theme' : ''} ${
                modal ? 'modal' : ''
            }`}
            onClick={() => {
                if (!coupon?.coupon) return;
                copyText(coupon?.coupon);
                onClickCard && onClickCard(coupon);
            }}
            style={customStyle}
        >
            <style global jsx>
                {`
                    .ant-message {
                        z-index: 999999;
                        .ant-message-notice .ant-message-notice-content {
                            border-radius: 6px;
                            overflow: hidden;
                        }
                    }
                    .ant-modal-root {
                        z-index: 9999;
                    }
                `}
            </style>
            {/* 背景 */}
            <div className={`bg ${customColor?.bgColor ? 'custom-bg' : ''}`} style={{ '--bg-color': customColor?.bgColor } as React.CSSProperties}>
                <EcoImg pc={getConponBg(coupon, false)} pad={getConponBg(coupon, false)} h5={getConponBg(coupon, true)} />
            </div>
            <div className="top">
                {/* 产品图 */}
                {coupon.ruleType !== 'COUPONS' && coupon.rulePic && (
                    <div className="product_pic">
                        <EcoImg pc={coupon.rulePic} h5={coupon.rulePic} />
                    </div>
                )}
                {/* 状态 */}
                <div className="reward-status">
                    {/* <EcoImg pc={'/img/ecocredits/pc/shape_bg.png'} /> */}
                    {/* <span className="status-text">{useStatusMap[useStatusC] || coupon?.useStatus}</span> */}
                    {useStatusMap[useStatusC] || coupon?.useStatus}
                </div>

                {/* 库存 - Left 50 */}
                {showstock && typeof coupon?.stock !== 'undefined' && <span className="stock">{t.rich('stock_left', { stock: coupon.stock as any })}</span>}

                {/* 金额 */}
                {!hideAmount && (
                    <div className={`amont ${coupon.discountType}`}>
                        {/* {['uk', 'br', 'mx', 'ph'].includes(locale as string) ? (
                            <div className="amount-content">
                                {coupon?.ruleType === 'GIFT_CARD' ? (
                                    `${formatPointsByLocale(Number(coupon?.amount), locale as string)}${coupon?.currencySymbol || '$'}`
                                ) : (
                                    <>
                                        {coupon.discountType !== 'percentage' ? (
                                            <>
                                                <div className="info">
                                                    <p className="unit">{coupon?.currencySymbol || '$'}</p>
                                                </div>
                                                {formatPointsByLocale(Number(coupon?.amount), locale as string)}
                                            </>
                                        ) : (
                                            <>
                                                {formatPointsByLocale(Number(coupon?.amount), locale as string)}
                                                <div className="info">
                                                    <p className="unit">{coupon.discountType === 'percentage' ? '%' : ''}</p>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="amount-content">
                                {coupon?.ruleType === 'GIFT_CARD' ? (
                                    coupon?.currencySymbol || '$'
                                ) : (
                                    <div className="info">
                                        <p className="unit">{coupon.discountType === 'percentage' ? '%' : coupon?.currencySymbol || '$'}</p>
                                    </div>
                                )}
                                {formatPointsByLocale(Number(coupon?.amount), locale as string)}
                            </div>
                        )} */}

                        <div className="amount-content">
                            {coupon?.ruleType === 'GIFT_CARD' ? (
                                !['uk', 'br', 'mx', 'ph'].includes(locale as string) ? (
                                    `${formatPointsByLocale(Number(coupon?.amount), locale as string)}${coupon?.currencySymbol || '$'}`
                                ) : (
                                    `${coupon?.currencySymbol || '$'}${formatPointsByLocale(Number(coupon?.amount), locale as string)}`
                                )
                            ) : (
                                <>
                                    {coupon.discountType !== 'percentage' ? (
                                        !['uk', 'br', 'mx', 'ph'].includes(locale as string) ? (
                                            <>
                                                {formatPointsByLocale(Number(coupon?.amount), locale as string)}
                                                <div className="info">
                                                    <p className="unit">{coupon?.currencySymbol || '$'}</p>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="info">
                                                    <p className="unit">{coupon?.currencySymbol || '$'}</p>
                                                </div>
                                                {formatPointsByLocale(Number(coupon?.amount), locale as string)}
                                            </>
                                        )
                                    ) : (
                                        <>
                                            {formatPointsByLocale(Number(coupon?.amount), locale as string)}
                                            <div className="info">
                                                <p className="unit">{coupon.discountType === 'percentage' ? '%' : ''}</p>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>

                        {/* 最低消费金额 */}
                        {coupon?.minimum_rule && <div className="minimum_rule">{coupon?.minimum_rule}</div>}
                    </div>
                )}
            </div>

            {/* <div className="box-center">
                积分显示 - 3,000 EcoCredits
                {!!coupon?.points && (
                    <p className="points">
                        <span className="points-value">{formatPointsByLocale(coupon.points, locale as string)}</span>{' '}
                        <span className="points-unit">{t('points')}</span>
                    </p>
                )}

                类型标签 - Discount code / Gift card
                <p className="coupon-type">{ruleTypeMap[coupon.ruleType] || ''}</p>
            </div> */}

            <div className="box-center">
                <Tooltip title={getCouponName(coupon)}>
                    <p className="type">{getCouponName(coupon)}</p>
                </Tooltip>

                {/* 实体产品订单id */}
                {isAllTrue(coupon?.orderName) && (
                    <div className="order-info">
                        <span>{t('order_text')}: </span>
                        <Link className="order-id" href={`https://${locale}.ecoflow.com/account`}>
                            {coupon?.orderName}
                        </Link>
                    </div>
                )}

                {/* 券码 */}
                {isAllTrue(coupon?.coupon, !coupon?.orderName) && (
                    <div className="reward-key">
                        <span>{coupon?.coupon}</span>
                        {!hideCopyIcon && (
                            <i
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    copyText(coupon?.coupon);
                                }}
                            >
                                <CopyIcon />
                            </i>
                        )}
                    </div>
                )}
            </div>

            {/* 倒计时 - 00:00:00:00 */}
            {isStartCountDown && (
                <div className="count-down">
                    {isStartCountDown && `${formatTime(days)} : ${formatTime(hours)} : ${formatTime(minutes)} : ${formatTime(seconds)}`}
                </div>
            )}

            {/* 兑换按钮 */}
            {/* {!hideBtn && (
                <div className="btn-wrapper">
                    <Button loading={loading} onClick={() => _onReedem(coupon)} disabled={disabled || disabledBtn} className="redeem-now">
                        {btnText || t('redeem_now')}
                    </Button>
                </div>
            )} */}

            {/* 规则链接 - Rules>> */}
            {!hideNote && (
                <div className="note-content">
                    <span
                        className="rules-link"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewsRules && onViewsRules(coupon);
                        }}
                    >
                        {t('rules')}
                    </span>
                </div>
            )}

            {/* 第三方优惠券链接-只有日本站会配置, 未提供国际化配置，先写死 */}
            {showJumpBtn && locale === 'jp' && (
                <div className="btn-wrapper jump-btn" style={{ visibility: coupon.useUrl ? 'visible' : 'hidden' }}>
                    <Link href={coupon.useUrl}>
                        <Button className="redeem-now">今すぐ交換する</Button>
                    </Link>
                </div>
            )}
        </div>
    );
};

export default Coupon;
